import { Component } from "react";
import * as Utils from "../../commons/Utils";
import { clone, debounce, isEqual } from "lodash";
import { ScreenUtils } from "oc-threejs/ScreenUtils";
import { MySvg } from "../../commons/MySvg";
import { v2, v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths from "oc-commons/Maths";
import * as Maths2 from "oc-threejs/Maths2";
import { Box2, Box3, Vector2 } from "three";

const polar = (angle, radius) =>
  v2(radius, 0).rotateAround(v2(), (((angle + 360) % 360) * Math.PI) / 180);

let currentMousePos = v2();
["mousemove", "touchmove"].forEach((name) =>
  window.addEventListener(name as any, (e: MouseEvent | TouchEvent) =>
    currentMousePos.set(
      (e as MouseEvent).pageX ?? (e as TouchEvent).changedTouches[0].pageX,
      (e as MouseEvent).pageY ?? (e as TouchEvent).changedTouches[0].pageY
    )
  )
);

export class ZaunCircleMenu extends Component {
  selectCircleObjects = null;
  selectSVG = null;

  scrollOrZooming = false;
  forbidUpdate = false;
  tictacsShouldUpdate = false;

  popupBoxes = [];
  popupSizes = [];

  _oldState = {
    füllungAuswahlwert: undefined,
    massAuswahlwert: undefined,
    selectedObjects: undefined,
    pfostenfarbeAuswahlwert: undefined,
    visible: undefined,
    activeTabKey: undefined,
    lastCenters: [],
    lastBoxes: [],
    lastSizes: [],
  };

  _scrollOrZoomingMousepos = currentMousePos.clone();
  _scrollOrZoomingCounter = 0;

  setScrollOrZooming(value) {
    // Selektion eines Elementes soll möglich schnell zur Anzeige des CircleMenus führen,
    // während Aktionen des OrbitControls ein Ausblenden erfordern und wegen des
    // Dampings nur Zeitverzögert passieren dürfen (daher die 500).

    // Achtung, OrbitControls meldet auch bei Klick ein orbitControlsStart / End, daher die
    // Erkennung zwischen Klick / Drag über Mouse-Verschiebung < 5 px

    this._scrollOrZoomingCounter += value ? 1 : -1;
    const mouseMovement = this._scrollOrZoomingMousepos.distanceTo(currentMousePos);
    this._scrollOrZoomingMousepos.copy(currentMousePos);
    const wasScrollOrZooming = this.scrollOrZooming;
    this.scrollOrZooming = this._scrollOrZoomingCounter > 0;

    if (!wasScrollOrZooming && this.scrollOrZooming) {
      this.update();
    } else if (wasScrollOrZooming && !this.scrollOrZooming) {
      if (mouseMovement < 5) {
        this.updateDebounced();
      } else {
        this.updateDebouncedLong();
      }
    }
  }

  // etwas Zeit geben, so dass sich die Selektoren auf vorhandene Dom-Objekte beziehen können
  updateDebounced = debounce(() => this.update(), 300);
  updateDebouncedLong = debounce(() => this.update(), 1000);

  update() {
    APP.zaunCircleMenu = this;
    if (this.forbidUpdate) {
      return;
    }

    const visible = APP.arrangement.selectedObjects[0] != null && !this.scrollOrZooming;

    const changedFüllung = this._oldState.füllungAuswahlwert !== APP.arrangement.füllungAuswahlwert;
    const changedMass = this._oldState.massAuswahlwert !== APP.arrangement.massAuswahlwert;
    const changedselectedObjects = Commons.arraysEqual(
      this._oldState.selectedObjects,
      APP.arrangement.selectedObjects
    );
    const changedTabKey = this._oldState.activeTabKey !== APP.arrangement.activeTabKey;
    const changedVisible = this._oldState.visible !== visible;
    const changedPfostenFarbe =
      this._oldState.pfostenfarbeAuswahlwert !== APP.arrangement.pfostenfarbeAuswahlwert;
    const changedDetails =
      changedselectedObjects ||
      changedMass ||
      changedFüllung ||
      changedPfostenFarbe ||
      changedTabKey;

    if (changedVisible) {
      if (visible) {
        this.showSelectCircle();
      } else {
        this.hideSelectCircle();
      }
    } else if (visible && changedDetails) {
      this.showSelectCircle();
    }

    this._oldState.füllungAuswahlwert = APP.arrangement.füllungAuswahlwert;
    this._oldState.massAuswahlwert = APP.arrangement.massAuswahlwert;
    this._oldState.selectedObjects = APP.arrangement.selectedObjects;
    this._oldState.activeTabKey = APP.arrangement.activeTabKey;
    this._oldState.visible = visible;
    this._oldState.pfostenfarbeAuswahlwert = APP.arrangement.pfostenfarbeAuswahlwert;
  }

  implodeSelectCircle() {
    this.selectSVG?.dashedCircle?.parent?.addClass("shrinkzero");
  }

  componentWillUnmount() {
    APP.arrangement.setSelectedObjects(null, false);
    this.hideSelectCircle();
  }

  getResponsiveParameters() {
    const responsiveFactor = APP.responsiveFactorWide;
    const stepAngle = 0.1;
    const startAngle = Maths.lerp(-80, -45, responsiveFactor);
    const ticTacPadding = Maths.lerp(2, 20, responsiveFactor);
    const innerCircleRadiusFactor = 1;
    const outerRadiusPadding = Maths.lerp(50, 150, responsiveFactor);
    return { stepAngle, startAngle, ticTacPadding, innerCircleRadiusFactor, outerRadiusPadding };
  }

  hideSelectCircle() {
    if (this.selectCircleObjects == null) {
      return;
    }
    this.selectCircleObjects = null;
    if (this.selectSVG) {
      const svg = this.selectSVG;
      this.selectSVG = null;
      svg.addClass("hidden");
      Commons.sleep(2000).then(() => {
        svg.remove();
      });
    }
    const popups = Utils.querySelectorAll(".zaunCircleMenuItem");
    popups.forEach((p) => p.classList.add("hidden"));
  }

  calculateBoxesOfPopups({ sphere, outerCircleRadius, circleRays, popupSizes }) {
    const { stepAngle, startAngle, ticTacPadding } = this.getResponsiveParameters();

    const box2 = (x0: number, y0: number, x1: number, y1: number) =>
      new Box3(v3(x0, y0, -1), v3(x1, y1, +1));

    const element2Box = (selector) => {
      const element = document.querySelector(selector);
      return element == null ? null : Utils.rectToBox(element.getBoundingClientRect(element));
    };

    const forbiddens: (Box2 | Box3)[] = [
      ".header",
      ".zaunelemente",
      ".material-menu",
      ".fuellung-menu",
      ".pfosten-profil-abschlussleisten-menu",
      ".scene-buttons",
      ".weiter-button",
      ".assistant",
    ]
      .map(element2Box)
      .filter((b) => b != null)
      .concat(circleRays);

    const c = element2Box("#cfg-app");

    // Sonderfall für mobile < 1000px Breite
    const isMobile = () => {
      return window.innerWidth < 1200;
    };
    let mobileTopDistance = 250;
    const mobilePadding = 12;

    // Sperrzonen aussen
    const inf = 10000;
    // left
    forbiddens.push(box2(c.min.x - inf, c.min.y, c.min.x, c.max.y));
    // top
    forbiddens.push(box2(c.min.x, c.min.y - inf, c.max.x, c.min.y));
    // right
    forbiddens.push(box2(c.max.x, c.min.y, c.max.x + inf, c.max.y));
    // bottom
    forbiddens.push(box2(c.min.x, c.max.y, c.max.x, c.max.y + inf));

    const boxPadding = (b, padding) =>
      box2(b.min.x - padding, b.min.y - padding, b.max.x + padding, b.max.y + padding);

    const isAllowedArea = (popupBox) =>
      forbiddens.every((forbidden) => !forbidden.intersectsBox(popupBox));

    const boxFromCenterSize = (c, s) =>
      box2(c.x - s.x / 2, c.y - s.y / 2, c.x + s.x / 2, c.y + s.y / 2);

    const sizes = clone(popupSizes);
    if (!isEqual(sizes, this.popupSizes)) {
      this.tictacsShouldUpdate = true;
      if (this.selectCircleObjects.length > 1) {
        this._oldState.lastCenters = [];
        this._oldState.lastBoxes = [];
        this._oldState.lastSizes = [];
      }
    }

    if (this.tictacsShouldUpdate) {
      this.popupSizes = clone(sizes);
      this.popupBoxes = [];
      const centers = [];
      const boxes = [];
      let sizeChanged = false;
      let lastSizeChanged = false;

      for (
        let sizeIndex = 0, angleIndex = 0;
        angleIndex < 360 / Math.abs(stepAngle) && sizeIndex < sizes.length;
        angleIndex++
      ) {
        const getCenterAndBox = (forIndex = 0): [Vector2, Box3] => {
          let center = v2();
          let box = new Box3();

          const circleAnglePoint = polar(angle, outerCircleRadius)
            .add(sphere.center)
            .add(v2(0, -sphere.radius / 2));

          const mobileCenterPoint = v2(
            window.innerWidth - 25 - this.popupSizes[forIndex].x / 2,
            mobileTopDistance
          );

          if (forIndex === 0) {
            center =
              this._oldState.lastCenters.length === 0
                ? isMobile()
                  ? mobileCenterPoint
                  : circleAnglePoint
                : this._oldState.lastCenters[forIndex];

            box =
              this._oldState.lastBoxes.length === 0
                ? boxFromCenterSize(center, sizes[forIndex])
                : this._oldState.lastBoxes[forIndex];
          } else {
            if (
              !isEqual(this._oldState.lastSizes[forIndex - 1]?.y, this.popupSizes[forIndex - 1]?.y)
            )
              lastSizeChanged = true;
            if (!isEqual(this._oldState.lastSizes[forIndex], this.popupSizes[forIndex]))
              sizeChanged = true;

            center =
              sizeChanged || lastSizeChanged || !this._oldState.lastCenters[forIndex]
                ? isMobile()
                  ? mobileCenterPoint
                  : circleAnglePoint
                : this._oldState.lastCenters[forIndex];

            box =
              sizeChanged || !this._oldState.lastBoxes[forIndex]
                ? boxFromCenterSize(center, this.popupSizes[forIndex])
                : this._oldState.lastBoxes[forIndex];
          }

          return [center, box];
        };

        const angle = startAngle + stepAngle * angleIndex;
        const [center, box] = getCenterAndBox(sizeIndex);

        if (!isMobile() && Maths2.box2IntersectsCircle(Maths3.box3To2(box), sphere)) {
          const clampedY = Math.max(box.min.y, Math.min(sphere.center.y, box.max.y));
          const dy = Math.abs(clampedY - sphere.center.y);

          if (dy < sphere.radius) {
            const dxNeeded = Math.sqrt(sphere.radius * sphere.radius - dy * dy);

            if (center.x >= sphere.center.x) {
              const targetMinX = sphere.center.x + dxNeeded;
              const shift = targetMinX - box.min.x;
              if (shift > 0) {
                box.min.x += shift;
                box.max.x += shift;
                center.x += shift;
              }
            } else {
              const targetMaxX = sphere.center.x - dxNeeded;
              const shift = targetMaxX - box.max.x;
              if (shift < 0) {
                box.min.x += shift;
                box.max.x += shift;
                center.x += shift;
              }
            }
          }
        }

        if (isAllowedArea(boxPadding(box, ticTacPadding)) || isMobile()) {
          mobileTopDistance +=
            this.popupSizes[sizeIndex].y / 2 +
            this.popupSizes[sizeIndex + 1]?.y / 2 +
            mobilePadding;
          this.popupBoxes.push(box);
          centers.push(center);
          boxes.push(box);
          forbiddens.push(box);
          sizeIndex++;
        }
      }
      this._oldState.lastCenters = centers;
      this._oldState.lastBoxes = boxes;
      this._oldState.lastSizes = clone(sizes);
    }

    return this.popupBoxes.map(Maths3.box3To2);
  }

  /**
   * Sollte sich einer dieser Werte in der Auswahl ändern werden die CirclemenuItems auf jeden Fall aktualisiert
   * @param elements {(Pfosten|Element|Empty)[]}
   * @returns {{isTor:boolean, isPfosten:boolean, isElement:boolean, isEmpty, id: number, isDoppeltor:boolean}[]}
   */
  toUpdateRepresentation(elements) {
    return elements.map((element) => {
      return {
        id: element.id,
        isTor: element.isTor ?? false,
        isPfosten: element.isPfosten ?? false,
        isElement: element.isElement ?? false,
        isDoppeltor: element.isDoppeltor ?? false,
        isEmpty: element.isEmpty ?? false,
      };
    });
  }

  showSelectCircle() {
    const { innerCircleRadiusFactor, outerRadiusPadding } = this.getResponsiveParameters();

    const objects = APP.arrangement.selectedObjects;
    const newSelectCircleObjects = this.toUpdateRepresentation(objects);
    this.tictacsShouldUpdate = !isEqual(newSelectCircleObjects, this.selectCircleObjects);
    this.selectCircleObjects = newSelectCircleObjects;

    if (this.tictacsShouldUpdate) {
      this._oldState.lastCenters = [];
      this._oldState.lastBoxes = [];
      this._oldState.lastSizes = [];
    }

    if (this.selectSVG) {
      this.selectSVG.remove();
    }

    const spheres = objects.filter((e) => e?.jaco3d != null).map((e) => e.jaco3d.getWorldSphere());

    if (spheres.length === 0) {
      return;
    }
    const worldSphere = spheres.reduce((a, b) => a.union(b));
    const sphere = new ScreenUtils().worldSphereToScreen(worldSphere);
    sphere.radius *= innerCircleRadiusFactor;
    sphere.radius += 20;

    const stage = document.querySelector(".stage") as HTMLElement;
    const svg = (this.selectSVG = new MySvg(stage).addClass("fade hidden"));

    svg.dashedCircle = svg
      .circle({
        ...sphere,
        radius: sphere.radius - 4,
      })
      .addClass("svg-dashed-line rotate");

    svg.dashedCircle.parent.addClass("shrinkzero-able");
    svg.dashedCircleAlt = svg
      .circle({
        ...sphere,
        radius: sphere.radius - 4,
      })
      .addClass("svg-dashed-line-alt rotate");
    svg.dashedCircleAlt.parent.addClass("shrinkzero-able");

    svg.push();

    const circleRays = [];

    const popups = Utils.querySelectorAll(".zaunCircleMenuItem");
    popups.forEach((p) => {
      p.classList.remove("display-none");
      p.classList.remove("hidden");
    });

    const popupSizes = [...popups].map((p) => {
      if (p == null) {
        return v2(40, 20);
      }
      const r = p.getBoundingClientRect();
      return v2(r.width, r.height);
    });

    const outerCircleRadius = sphere.radius + outerRadiusPadding;

    const popupBoxes = this.calculateBoxesOfPopups({
      sphere,
      outerCircleRadius,
      circleRays,
      popupSizes,
    });
    popupBoxes.forEach((box, index) => {
      const popup = popups[index];

      const direction = box.getCenter(v2()).sub(sphere.center).normalize();
      const lineStartOnSphere = sphere.center
        .clone()
        .add(direction.clone().multiplyScalar(sphere.radius));

      svg.line(lineStartOnSphere, box.getCenter(v2()));

      popup.style.left = box.min.x + "px";
      popup.style.top = box.min.y + "px";
      popup.classList.remove("hidden");
    });

    const g1 = svg.svg;
    const g2 = g1.clone();
    svg.pop();

    g1.addClass("svg-line-outer");
    g2.addClass("svg-line-inner");

    svg.removeClass("hidden");

    STAGE.dirty();
  }

  render() {
    this.updateDebounced();

    return <nav aria-label={I18N("aria::Kontextmenü für Zaunelemente")}>{this.props.children}</nav>;
  }
}
